import type { Card } from '../game/data/cards/types';
import type { TribeProgress } from './synergyProgress';
import { EDGES } from '../theme/tokens';

// One tribe, fully explained: the verb, the fantasy, and all three rungs of
// the 2/4/6 ladder with your position on it.
//
// This is where `verb`, `fantasy` and the locked breakpoint descriptions
// finally reach the player. All three have been authored in tribes.ts since
// the power-fantasy pass; none of them were rendered anywhere.
//
// The framing that matters: breakpoints REPLACE each other. Reaching 4 does
// not add to the 2 payoff, it supersedes it (getActiveBreakpoint, highest
// wins). Every string here says "at 4" / "replaces", never "also".

function hexFromColorInt(n: number): string {
  return `#${n.toString(16).padStart(6, '0')}`;
}

interface TribeDetailProps {
  progress: TribeProgress;
  /** Fielded units of this tribe, named so the player can see what counts. */
  units?: Card[];
}

export function TribeDetail({ progress, units = [] }: TribeDetailProps) {
  const { tribe, count, fromHand, fromShop, active, next, toNext } = progress;
  const tribeColor = hexFromColorInt(tribe.color);
  const factionFont = tribe.faction === 'cholo' ? 'font-cholo' : 'font-emo';

  return (
    <section
      className="rounded-md border bg-stucco1 p-sm md:p-md"
      style={{
        borderColor: active ? `${tribeColor}66` : EDGES.soft,
        boxShadow: active ? `0 0 10px ${tribeColor}33` : undefined,
      }}
      aria-label={`${tribe.name}, ${count} fielded`}
    >
      {/* Name + verb + live count */}
      <header className="flex items-baseline justify-between gap-sm">
        <div className="flex min-w-0 flex-1 flex-col">
          <span
            className={`truncate text-[16px] leading-tight md:text-[19px] ${factionFont}`}
            style={{ color: tribeColor }}
          >
            {tribe.name}
          </span>
          <span
            className="font-data text-[9px] uppercase tracking-[0.3em] md:text-[10px]"
            style={{ color: active ? tribeColor : '#8a7e6c' }}
          >
            {tribe.verb}
          </span>
        </div>
        <div className="shrink-0 text-right">
          <span
            className="font-data text-[18px] font-bold leading-none md:text-[22px]"
            style={{ color: active ? '#f5ecd6' : '#8a7e6c' }}
          >
            {count}
          </span>
          <span className="font-data text-[10px] text-graphite">/6</span>
        </div>
      </header>

      {/* The fantasy line — what this tribe lets you do, in player language */}
      <p className="mt-xs text-[11px] leading-snug text-marker md:text-[12px]">
        {tribe.fantasy}
      </p>

      {/* Progress hint + potential from hand / shop */}
      <div className="mt-xs flex flex-wrap items-center gap-x-sm gap-y-[2px]">
        {next ? (
          <span className="font-data text-[9px] uppercase tracking-[0.2em] text-bone md:text-[10px]">
            {toNext} more → {next.threshold}
          </span>
        ) : (
          <span className="font-data text-[9px] uppercase tracking-[0.2em] text-glowStick md:text-[10px]">
            maxed
          </span>
        )}
        {fromHand > 0 && (
          <span className="font-data text-[9px] uppercase tracking-[0.2em] text-iceCrystal md:text-[10px]">
            +{fromHand} in hand
          </span>
        )}
        {fromShop > 0 && (
          <span className="font-data text-[9px] uppercase tracking-[0.2em] text-sodium md:text-[10px]">
            ◆ {fromShop} in shop
          </span>
        )}
      </div>

      {/* The 2/4/6 ladder. Every rung visible whether or not it's reached —
          this is the thing the old chip could not show. */}
      <ol className="mt-sm flex flex-col gap-[3px]">
        {tribe.breakpoints.map((bp) => {
          const hit = count >= bp.threshold;
          const isActive = active?.threshold === bp.threshold;
          const isNext = next?.threshold === bp.threshold;
          return (
            <li
              key={bp.threshold}
              className={`flex gap-xs rounded-sm px-xs py-[3px] ${
                isActive ? 'bg-stucco2' : ''
              }`}
              style={{
                // Only the rung that is actually paying out gets the tribe
                // colour. A hit-but-superseded rung is dimmed like a locked
                // one, because mechanically it is no longer doing anything.
                borderLeft: `2px solid ${
                  isActive ? tribeColor : isNext ? '#8a7e6c' : 'transparent'
                }`,
              }}
            >
              <span
                className="shrink-0 font-data text-[11px] font-bold leading-snug md:text-[12px]"
                style={{ color: isActive ? tribeColor : hit ? '#c8b89a' : '#5a5248' }}
              >
                {bp.threshold}
              </span>
              <span
                className={`flex-1 text-[10px] leading-snug md:text-[11px] ${
                  isActive ? 'text-bone' : hit ? 'text-marker' : 'text-graphite'
                }`}
              >
                {bp.description}
              </span>
            </li>
          );
        })}
      </ol>

      {/* Replacement framing — stated once, not repeated per rung. Without
          this a player reasonably reads the ladder as cumulative and
          overestimates what 6 is worth. */}
      <p className="mt-xs font-data text-[8px] uppercase tracking-[0.2em] text-graphite md:text-[9px]">
        only the highest rung you reach applies
      </p>

      {/* Which of your units count — turns an abstract number into the
          specific cards on your board. */}
      {units.length > 0 && (
        <p className="mt-xs border-t pt-xs text-[10px] leading-snug text-graphite md:text-[11px]"
           style={{ borderColor: EDGES.soft }}>
          <span className="font-data uppercase tracking-[0.2em]">yours: </span>
          {units.map((u) => u.name).join(' · ')}
        </p>
      )}
    </section>
  );
}
